import { Component, OnInit, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatPaginator } from '@angular/material/paginator';
import { AuthService } from '../../api-authorization/authorize.service';
import { ElementWithdrawalService } from '../../Services/element-withdrawal.service';
import { ElementReturnService } from '../../Services/element-return.service';
import { ResourcePlanningService } from '../../Services/resource-planning.service';
import { UserModel } from '../../user.model';
import { WithdrawalLogModel } from '../../Models/withdrawal-log.model';
import { ReturnLogModel } from '../../Models/return-log.model';
import { utcToZonedTime, format } from 'date-fns-tz';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { PageEvent } from '@angular/material/paginator';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { environment } from '../../environments/environment';
import { MyAbsence, ResourceCategoryDef } from '../../Models/resource-assignment.model';

interface Activity {
  id: number;
  action: string;
  timestamp: string;
  userId: string;
}

@Component({
  selector: 'app-user-dashboard',
  templateUrl: './user-dashboard.component.html',
  styleUrls: ['./user-dashboard.component.css']
})
export class UserDashboardComponent implements OnInit, AfterViewInit {
  user: UserModel | null = null;
  loading = true;
  recentActivities: Activity[] = [];
  displayedActivities: Activity[] = [];
  error: string | null = null;
  length = 0;
  pageSize = 5;

  // Signature
  signatureUrl: string | null = null;
  editingSignature = false;
  savingSignature = false;
  sigDrawn = false;
  private sigCtx: CanvasRenderingContext2D | null = null;
  private sigDrawing = false;
  @ViewChild('sigCanvas') sigCanvas!: ElementRef<HTMLCanvasElement>;
  pageIndex = 0;

  // Withdrawal data
  userWithdrawals: WithdrawalLogModel[] = [];
  displayedWithdrawals: WithdrawalLogModel[] = [];
  withdrawalsLoading = false;
  withdrawalPageIndex = 0;
  withdrawalColumns: string[] = ['element', 'quantity', 'purpose', 'date', 'status'];

  // Return data
  userReturns: ReturnLogModel[] = [];
  displayedReturns: ReturnLogModel[] = [];
  returnsLoading = false;
  returnPageIndex = 0;
  returnColumns: string[] = ['element', 'quantity', 'reason', 'date'];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // Kalender teilen (Self-Service)
  calendarFeedUrl = '';
  feedSaving = false;

  // Abwesenheiten (Self-Service)
  myAbsences: MyAbsence[] = [];
  absenceCategories: ResourceCategoryDef[] = [];
  absCategoryId: number | null = null;
  absStart = '';
  absEnd = '';
  absNotes = '';
  absSaving = false;

  constructor(
    private authService: AuthService,
    private withdrawalService: ElementWithdrawalService,
    private returnService: ElementReturnService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private planningService: ResourcePlanningService
  ) { }

  ngOnInit() {
    this.authService.getCurrentUser()
      .subscribe(
        (user) => {
          this.user = user;
          this.loadUserActivities();
          this.loadUserWithdrawals();
          this.loadUserReturns();
          this.loadSignature();
          this.loadMyFeed();
          this.loadMyAbsences();
        },
        (error) => {
          console.error('Fehler beim Abrufen der Benutzerdaten:', error);
          this.error = 'Fehler beim Abrufen der Benutzerdaten. Bitte versuchen Sie es später erneut.';
          this.loading = false;
        }
      );
  }

  ngAfterViewInit() {
    this.updateDisplayedActivities();
  }

  // ---- Activities ----
  loadUserActivities() {
    this.authService.getUserActivities()
      .subscribe(
        (activities: Activity[]) => {
          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          this.recentActivities = activities.map((activity: Activity) => ({
            ...activity,
            timestamp: this.convertToUserTimeZone(activity.timestamp, timeZone)
          }));
          this.length = this.recentActivities.length;
          this.updateDisplayedActivities();
          this.loading = false;
        },
        (error) => {
          console.error('Fehler beim Laden der Aktivitäten:', error);
          this.error = 'Fehler beim Laden der Aktivitäten. Bitte versuchen Sie es später erneut.';
          this.loading = false;
        }
      );
  }

  // ---- Withdrawals ----
  loadUserWithdrawals() {
    this.withdrawalsLoading = true;
    this.withdrawalService.getMyWithdrawals()
      .subscribe(
        (withdrawals: WithdrawalLogModel[]) => {
          this.userWithdrawals = withdrawals.sort((a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
          this.updateDisplayedWithdrawals();
          this.withdrawalsLoading = false;
        },
        (error) => {
          console.error('Fehler beim Laden der Entnahmen:', error);
          this.withdrawalsLoading = false;
        }
      );
  }

  // ---- Returns ----
  loadUserReturns() {
    this.returnsLoading = true;
    this.returnService.getMyReturns()
      .subscribe(
        (returns: ReturnLogModel[]) => {
          this.userReturns = returns.sort((a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
          this.updateDisplayedReturns();
          this.returnsLoading = false;
        },
        (error) => {
          console.error('Fehler beim Laden der Rückgaben:', error);
          this.returnsLoading = false;
        }
      );
  }

  // ---- Pagination: Activities ----
  updateDisplayedActivities() {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.displayedActivities = this.recentActivities.slice(start, end);
  }

  handlePageEvent(event: PageEvent) {
    this.length = event.length;
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.updateDisplayedActivities();
  }

  // ---- Pagination: Withdrawals ----
  updateDisplayedWithdrawals() {
    const start = this.withdrawalPageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.displayedWithdrawals = this.userWithdrawals.slice(start, end);
  }

  handleWithdrawalPageEvent(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.withdrawalPageIndex = event.pageIndex;
    this.updateDisplayedWithdrawals();
  }

  // ---- Pagination: Returns ----
  updateDisplayedReturns() {
    const start = this.returnPageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.displayedReturns = this.userReturns.slice(start, end);
  }

  handleReturnPageEvent(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.returnPageIndex = event.pageIndex;
    this.updateDisplayedReturns();
  }

  // ---- Tab Change ----
  onTabChange(event: MatTabChangeEvent) {
    // Optionally trigger loading on tab change
  }

  // ---- Helpers ----
  getTotalWithdrawnQuantity(): number {
    return this.userWithdrawals.reduce((sum, w) => sum + w.quantity, 0);
  }

  formatDate(dateString: string): string {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.convertToUserTimeZone(dateString, timeZone);
  }

  convertToUserTimeZone(dateString: string, timeZone: string): string {
    const utcDate = new Date(dateString + 'Z');
    const zonedDate = utcToZonedTime(utcDate, timeZone);
    return format(zonedDate, 'dd.MM.yyyy HH:mm', { timeZone });
  }

  // ---- Kalender teilen (Self-Service) ----
  loadMyFeed(): void {
    this.planningService.getMyFeed().subscribe({
      next: res => this.calendarFeedUrl = res?.url || '',
      error: () => { /* still ok */ }
    });
  }

  saveMyFeed(): void {
    this.feedSaving = true;
    const url = this.calendarFeedUrl.trim();
    this.planningService.setMyFeed(url || null).subscribe({
      next: () => {
        this.feedSaving = false;
        this.snackBar.open('Kalender-Link gespeichert.', 'OK', { duration: 3000 });
      },
      error: () => {
        this.feedSaving = false;
        this.snackBar.open('Ungültiger Link oder Fehler beim Speichern.', 'OK', { duration: 5000 });
      }
    });
  }

  clearMyFeed(): void {
    this.calendarFeedUrl = '';
    this.saveMyFeed();
  }

  // ---- Abwesenheiten (Self-Service) ----
  loadMyAbsences(): void {
    this.planningService.getMyAbsences().subscribe({
      next: res => this.myAbsences = res || [],
      error: () => { /* still ok */ }
    });
    this.planningService.getMyAbsenceCategories().subscribe({
      next: res => {
        this.absenceCategories = res || [];
        if (this.absCategoryId == null && this.absenceCategories.length) {
          this.absCategoryId = this.absenceCategories[0].id;
        }
      },
      error: () => { /* still ok */ }
    });
  }

  get absValid(): boolean {
    return !!this.absStart && !!this.absEnd && new Date(this.absEnd) > new Date(this.absStart);
  }

  requestAbsence(): void {
    if (!this.absValid) {
      return;
    }
    this.absSaving = true;
    this.planningService.requestMyAbsence({
      categoryId: this.absCategoryId,
      start: this.absStart.length === 16 ? this.absStart + ':00' : this.absStart,
      end: this.absEnd.length === 16 ? this.absEnd + ':00' : this.absEnd,
      notes: this.absNotes.trim() || undefined
    }).subscribe({
      next: () => {
        this.absSaving = false;
        this.absNotes = '';
        this.snackBar.open('Abwesenheit beantragt.', 'OK', { duration: 3000 });
        this.loadMyAbsences();
      },
      error: () => {
        this.absSaving = false;
        this.snackBar.open('Antrag fehlgeschlagen (Zeitraum prüfen).', 'OK', { duration: 5000 });
      }
    });
  }

  cancelAbsence(a: MyAbsence): void {
    if (!confirm('Antrag/Abwesenheit zurückziehen?')) {
      return;
    }
    this.planningService.cancelMyAbsence(a.id).subscribe({
      next: () => {
        this.snackBar.open('Antrag zurückgezogen.', 'OK', { duration: 3000 });
        this.loadMyAbsences();
      },
      error: () => this.snackBar.open('Zurückziehen fehlgeschlagen.', 'OK', { duration: 4000 })
    });
  }

  absenceStatusLabel(status: string): string {
    switch (status) {
      case 'Pending': return 'Beantragt';
      case 'Approved': return 'Genehmigt';
      case 'Rejected': return 'Abgelehnt';
      default: return status;
    }
  }

  absenceStatusColor(status: string): string {
    switch (status) {
      case 'Pending': return '#f57c00';
      case 'Approved': return '#2e7d32';
      case 'Rejected': return '#c62828';
      default: return '#90a4ae';
    }
  }

  logout() {
    this.authService.logout();
  }

  resetPassword() {
    const dialogRef = this.dialog.open(ChangePasswordComponent, {
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(success => {
      // Der Dialog zeigt Fehler jetzt selbst inline an; hier nur die Erfolgsmeldung.
      if (success) {
        this.snackBar.open('Passwort erfolgreich geändert', 'Schließen', { duration: 3000 });
      }
    });
  }

  // ---- Signature ----
  private getSignatureBaseUrl(): string {
    // Strip /api from apiBaseUrl to get the server root
    return environment.apiBaseUrl.replace(/\/api\/?$/, '');
  }

  loadSignature(): void {
    this.authService.getSignature().subscribe({
      next: (res) => {
        this.signatureUrl = res.hasSignature && res.url ? this.getSignatureBaseUrl() + res.url : null;
      },
      error: () => {}
    });
  }

  startEditingSignature(): void {
    this.editingSignature = true;
    this.sigDrawn = false;
    setTimeout(() => this.initSignatureCanvas(), 150);
  }

  cancelEditingSignature(): void {
    this.editingSignature = false;
    this.sigDrawn = false;
  }

  private initSignatureCanvas(): void {
    if (!this.sigCanvas) return;
    const canvas = this.sigCanvas.nativeElement;
    if (!canvas || !canvas.offsetWidth) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    this.sigCtx = ctx;

    const getPos = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const getTouchPos = (e: TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    };

    canvas.addEventListener('mousedown', (e) => {
      this.sigDrawing = true;
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    });
    canvas.addEventListener('mousemove', (e) => {
      if (!this.sigDrawing) return;
      const pos = getPos(e);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      this.sigDrawn = true;
    });
    canvas.addEventListener('mouseup', () => this.sigDrawing = false);
    canvas.addEventListener('mouseleave', () => this.sigDrawing = false);
    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.sigDrawing = true;
      const pos = getTouchPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    }, { passive: false });
    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (!this.sigDrawing) return;
      const pos = getTouchPos(e);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      this.sigDrawn = true;
    }, { passive: false });
    canvas.addEventListener('touchend', () => this.sigDrawing = false);
  }

  clearSignatureCanvas(): void {
    if (!this.sigCtx || !this.sigCanvas) return;
    const canvas = this.sigCanvas.nativeElement;
    this.sigCtx.clearRect(0, 0, canvas.width, canvas.height);
    this.sigDrawn = false;
  }

  saveSignature(): void {
    if (!this.sigCanvas || !this.sigDrawn) return;
    this.savingSignature = true;
    const dataUrl = this.sigCanvas.nativeElement.toDataURL('image/png');
    this.authService.uploadSignature(dataUrl).subscribe({
      next: (res) => {
        this.signatureUrl = res.url ? this.getSignatureBaseUrl() + res.url : null;
        this.editingSignature = false;
        this.savingSignature = false;
        this.snackBar.open('Unterschrift gespeichert', 'OK', { duration: 3000 });
      },
      error: () => {
        this.savingSignature = false;
        this.snackBar.open('Fehler beim Speichern', 'OK', { duration: 4000 });
      }
    });
  }

  deleteSignature(): void {
    this.authService.deleteSignature().subscribe({
      next: () => {
        this.signatureUrl = null;
        this.editingSignature = false;
        this.snackBar.open('Unterschrift gelöscht', 'OK', { duration: 3000 });
      },
      error: () => {
        this.snackBar.open('Fehler beim Löschen', 'OK', { duration: 4000 });
      }
    });
  }
}
